import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Play, FileText, Download } from 'lucide-react';
import { getCourseById, toggleLessonCompletion } from '../services/firebase/courses';
import { useAuth } from '../contexts/AuthContext';
import PortalBackground from '../components/PortalBackground';

console.log('CourseContent.js file loaded');

const CourseContent = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { currentUser: user, loading: authLoading } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [completingLessons, setCompletingLessons] = useState(new Set());
  
  console.log('Auth state - user:', user, 'user?.uid:', user?.uid, 'authLoading:', authLoading);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const courseData = await getCourseById(courseId);
        if (courseData) {
          // Sort modules by order field
          const sortedModules = (courseData.modules || []).sort((a, b) => (a.order || 0) - (b.order || 0));
          
          // Sort lessons within each module by order field
          const modulesWithSortedLessons = sortedModules.map(module => ({
            ...module,
            lessons: (module.lessons || []).sort((a, b) => (a.order || 0) - (b.order || 0))
          }));
          
          const sortedCourseData = {
            ...courseData,
            modules: modulesWithSortedLessons
          };
          
          setCourse(sortedCourseData);

          // Deep-link: if the URL points at a specific lesson, open it; otherwise default to the first lesson.
          let targetModule = null;
          let targetLesson = null;
          if (lessonId) {
            for (const module of sortedCourseData.modules || []) {
              const lesson = module.lessons?.find(l => l.id === lessonId);
              if (lesson) {
                targetModule = module;
                targetLesson = lesson;
                break;
              }
            }
          }
          if (!targetLesson && sortedCourseData.modules?.[0]?.lessons?.[0]) {
            targetModule = sortedCourseData.modules[0];
            targetLesson = sortedCourseData.modules[0].lessons[0];
          }

          setExpandedModules(targetModule ? [targetModule.id] : []);
          if (targetLesson && targetModule) {
            setSelectedLesson({
              ...targetLesson,
              moduleId: targetModule.id,
              moduleTitle: targetModule.title
            });
          }
        } else {
          setError('Course not found');
        }
      } catch (err) {
        console.error('Error fetching course:', err);
        setError('Failed to load course content. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [courseId, lessonId]);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleLessonClick = (lesson, module) => {
    setSelectedLesson({ ...lesson, moduleId: module.id, moduleTitle: module.title });
    setVideoLoading(true);
    // Reset video loading after a short delay to allow iframe to load
    setTimeout(() => setVideoLoading(false), 1000);
  };

  // Handle video end event to auto-complete lesson
  const handleVideoEnd = () => {
    if (selectedLesson && !selectedLesson.completed) {
      // Find the module for the current lesson
      const currentModule = course?.modules?.find(m => m.id === selectedLesson.moduleId);
      if (currentModule) {
        handleLessonCompletion(selectedLesson, currentModule, true);
      }
    }
  };

  const getDocumentDownloadUrl = (doc) =>
    doc?.url || doc?.downloadURL || doc?.downloadUrl || doc?.fileUrl || '';

  // Helper: check if lesson has documents to download
  const hasDocuments = (lesson) =>
    Array.isArray(lesson.documents) &&
    lesson.documents.some((d) => !!getDocumentDownloadUrl(d));

  // Helper: handle document download
  const handleDocumentDownload = async (docItem) => {
    const fileUrl = getDocumentDownloadUrl(docItem);
    try {
      if (!fileUrl) {
        console.error('No URL provided for document');
        return;
      }

      // For Firebase Storage URLs or direct file URLs
      if (fileUrl.startsWith('https://firebasestorage.googleapis.com') ||
          fileUrl.startsWith('http') ||
          fileUrl.startsWith('blob:')) {
        const link = window.document.createElement('a');
        link.href = fileUrl;
        link.download = docItem.displayName || docItem.name || 'document';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      } else {
        const response = await fetch(fileUrl);
        if (response.ok) {
          const blob = await response.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          const link = window.document.createElement('a');
          link.href = objectUrl;
          link.download = docItem.displayName || docItem.name || 'document';
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
          window.URL.revokeObjectURL(objectUrl);
        } else {
          console.error('Failed to fetch document:', response.statusText);
        }
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      window.open(fileUrl, '_blank');
    }
  };

  // Handle lesson completion toggle
  const handleLessonCompletion = async (lesson, module, completed) => {
    try {
      console.log('User object:', user);
      console.log('User UID:', user?.uid);
      console.log('Auth loading:', authLoading);
      
      if (authLoading) {
        console.log('Authentication still loading, please wait...');
        alert('Please wait for authentication to complete');
        return;
      }
      
      const userId = user?.uid;
      if (!userId) {
        console.error('User not authenticated - user object:', user);
        alert('Please log in to mark lessons as complete');
        return;
      }

      // Add lesson to completing set
      setCompletingLessons(prev => new Set(prev).add(lesson.id));

      await toggleLessonCompletion(courseId, module.id, lesson.id, completed, userId);
      
      // Update local state to reflect the change
      setCourse(prevCourse => ({
        ...prevCourse,
        modules: prevCourse.modules.map(m => {
          if (m.id === module.id) {
            return {
              ...m,
              lessons: m.lessons.map(l => {
                if (l.id === lesson.id) {
                  return { ...l, completed };
                }
                return l;
              })
            };
          }
          return m;
        })
      }));

      // Update selected lesson if it's the current one
      if (selectedLesson && selectedLesson.id === lesson.id) {
        setSelectedLesson(prev => ({ ...prev, completed }));
      }

      // Trigger a custom event to notify CourseDashboard to refresh progress
      window.dispatchEvent(new CustomEvent('lessonProgressUpdated', {
        detail: { courseId, lessonId: lesson.id, completed }
      }));
    } catch (error) {
      console.error('Error toggling lesson completion:', error);
      // You could add a toast notification here
    } finally {
      // Remove lesson from completing set
      setCompletingLessons(prev => {
        const newSet = new Set(prev);
        newSet.delete(lesson.id);
        return newSet;
      });
    }
  };

  if (authLoading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading course content...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error</h3>
          <p className="text-gray-600 dark:text-gray-400">{error || 'Course not found'}</p>
          <button
            onClick={() => navigate('/portal/courses')}
            className="mt-4 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            Return to Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex flex-col">
      <PortalBackground />
      <div className="relative flex flex-col flex-1 min-h-0">
      {/* Page Header with Course Title */}
      <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-center relative">
          <button 
            onClick={() => navigate('/portal/courses')} 
            className="absolute left-0 flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white text-center">
            {course.title}
          </h1>
        </div>
      </div>
      
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-full md:w-80 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 flex flex-col">
          <div className="flex-1 overflow-y-auto">
            {course.modules?.map((module, mIdx) => {
              return (
                <div key={module.id}>
                  {/* Module header */}
                  <div
                    className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => toggleModule(module.id)}
                  >
                    <div className="flex items-center">
                      {expandedModules.includes(module.id) ? (
                        <ChevronDown className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" />
                      )}
                      <span className="font-semibold text-base dark:text-white">{module.title}</span>
                    </div>
                  </div>
                  {/* Lessons */}
                  {expandedModules.includes(module.id) && (
                    <div>
                      {module.lessons?.map((lesson, lIdx) => (
                        <div
                          key={lesson.id}
                          className={`flex items-center justify-between px-8 py-3 border-b border-gray-100 dark:border-gray-600 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                            selectedLesson && selectedLesson.id === lesson.id && selectedLesson.moduleId === module.id 
                              ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-l-red-500' 
                              : 'bg-gray-50 dark:bg-gray-700'
                          }`}
                          onClick={() => handleLessonClick(lesson, module)}
                        >
                          <div className="flex items-center space-x-2">
                            <span className={`font-bold text-lg ${
                              selectedLesson && selectedLesson.id === lesson.id && selectedLesson.moduleId === module.id
                                ? 'text-red-600 dark:text-red-400'
                                : 'dark:text-white'
                            }`}>{lIdx + 1}.</span>
                            <span className={`font-semibold ${
                              selectedLesson && selectedLesson.id === lesson.id && selectedLesson.moduleId === module.id
                                ? 'text-red-600 dark:text-red-400'
                                : 'dark:text-white'
                            }`}>{lesson.title}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {selectedLesson && selectedLesson.id === lesson.id && selectedLesson.moduleId === module.id && (
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLessonCompletion(lesson, module, !lesson.completed);
                              }}
                              disabled={completingLessons.has(lesson.id)}
                              className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                                lesson.completed 
                                  ? 'bg-green-500 border-green-500 hover:bg-green-600' 
                                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-100'
                              } ${completingLessons.has(lesson.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={lesson.completed ? 'Mark as incomplete' : 'Mark as complete'}
                            >
                              {completingLessons.has(lesson.id) ? (
                                <div className="animate-spin rounded-full h-2 w-2 border-b border-gray-600"></div>
                              ) : lesson.completed ? (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : null}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col min-h-0">
          {/* Video/Image area */}
          <div className="flex flex-col items-center relative p-4">
            {selectedLesson ? (
                              <div className="w-full flex items-center justify-center">
                {selectedLesson.videoUrl ? (
                  <div className="relative w-full max-w-6xl">
                    {videoLoading && (
                      <div className="absolute inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center rounded-lg z-10">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                          <p className="text-white text-sm">Loading video...</p>
                        </div>
                      </div>
                    )}
                    <video
                      key={selectedLesson.id}
                      controls
                      controlsList="nodownload"
                      className="w-full h-auto rounded-lg shadow-lg"
                      onLoadedData={() => setVideoLoading(false)}
                      onEnded={handleVideoEnd}
                    >
                      {/* No type attribute: lessons can be MP4, MOV, M4V or WebM — let the browser sniff it */}
                      <source key={selectedLesson.videoUrl} src={selectedLesson.videoUrl} />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-64 h-48 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-4">
                      <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                      No video available for this lesson
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                {course.thumbnailUrl ? (
                  <div className="w-full max-w-4xl mx-auto">
                    <img 
                      src={course.thumbnailUrl} 
                      alt={`${course.title} thumbnail`} 
                      className="w-full h-auto max-h-80 object-contain rounded-lg shadow-lg"
                    />
                    <p className="mt-4 text-gray-600 dark:text-gray-400">
                      Select a lesson to view content
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="w-64 h-48 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-4">
                      <Play className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                      Select a lesson to view content
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Description */}
          <div className="p-6">
            <h2 className="text-xl font-bold mb-2 dark:text-white text-center">
              Description
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              {selectedLesson ? selectedLesson.description : course.description}
            </p>
            
            
            {/* Document Download */}
            {selectedLesson && hasDocuments(selectedLesson) && (
              <div className="mt-6 flex flex-row space-x-3">
                {selectedLesson.documents
                  .filter((d) => getDocumentDownloadUrl(d))
                  .map((docItem, index) => (
                  <button
                    key={`${getDocumentDownloadUrl(docItem)}-${index}`}
                    onClick={() => handleDocumentDownload(docItem)}
                    className="inline-flex items-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {docItem.displayName || docItem.name || 'Download Document'}
                  </button>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
      </div>
    </div>
  );
};

export default CourseContent; 